import { useCallback, createContext, useContext } from "react";
import { ARCGIS_SERVICES_ROOT_URL } from "./settings";
import { geoProcessor } from "./useEsriLoader";

const EsriContext = createContext();

const EsriProvider = ({ esriLoader, children }) => {
  const setLayerVisible = useCallback(
    (layerId, visible) => {
      if (layerId || esriLoader.view) {
        const getFeature = esriLoader.view.map.findLayerById(layerId);
        if (getFeature) {
          esriLoader.view.whenLayerView(getFeature).then(function (layerView) {
            layerView.visible = visible;
          });
        }
      }
    },
    [esriLoader]
  );

  const addLayer = useCallback(
    (options) => {
      if (!options) return;
      const FeatureLayer = new esriLoader.FeatureLayer(options);
      esriLoader.view.map.add(FeatureLayer);
    },
    [esriLoader]
  );

  const getFeatureLayer = useCallback(
    (layerId) => {
      if (!layerId) return;
      return new Promise((resolve, reject) => {
        const getFeature = esriLoader.view.map.findLayerById(layerId);
        if (getFeature) {
          getFeature.load().then((layer) => {
            resolve(layer);
          });
        } else {
          reject({ loadStatus: "none" });
        }
      });
    },
    [esriLoader]
  );

  const removeLayer = useCallback(
    (layerId) => {
      if (!layerId) {
        return;
      }
      const getFeature = esriLoader.view.map.findLayerById(layerId);
      if (getFeature) {
        esriLoader.view.map.remove(getFeature);
      }
    },
    [esriLoader]
  );

  const layerOpacity = useCallback(
    (layerId, opacityValue) => {
      if (!esriLoader) return;
      const getFeature = esriLoader.view.map.findLayerById(layerId);
      if (getFeature) {
        if (opacityValue >= 0 && opacityValue <= 1) {
          getFeature.opacity = opacityValue;
        }
      }
    },
    [esriLoader]
  );

  const registerToken = useCallback(
    (tokenJSON) => {
      if (!tokenJSON) return;
      esriLoader.IdentityManager.registerToken(tokenJSON);
    },
    [esriLoader]
  );

  const destroyToken = useCallback(() => {
    const credential = esriLoader.IdentityManager.findCredential(
      ARCGIS_SERVICES_ROOT_URL
    );
    if (credential) {
      esriLoader.IdentityManager.destroyCredentials();
      credential.destroy();
    }
  }, [esriLoader]);

  const getToken = useCallback(() => {
    const credential = esriLoader.IdentityManager.findCredential(
      ARCGIS_SERVICES_ROOT_URL
    );
    if (credential) {
      return credential;
    }
    return;
  }, [esriLoader]);

  const resetMapZoom = useCallback(() => {
    esriLoader.view.zoom = 7;
  }, [esriLoader]);

  const clearPopup = useCallback(() => {
    esriLoader.view.popup.close();
  }, [esriLoader]);

  const methods = {
    setLayerVisible,
    addLayer,
    removeLayer,
    geoProcessor,
    resetMapZoom,
    esriLoader,
    registerToken,
    destroyToken,
    layerOpacity,
    getFeatureLayer,
    clearPopup,
    getToken,
  };

  return (
    <EsriContext.Provider value={methods}>{children}</EsriContext.Provider>
  );
};

const useEsri = () => {
  const context = useContext(EsriContext);
  if (context === undefined) {
    throw new Error("must be used within <EsriProvider/>");
  }
  return context;
};

export { EsriProvider, useEsri };
